import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Eye, EyeOff, Shield, RefreshCw, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { API } from '../App';
import ForcePasswordChange from './ForcePasswordChange';

function TitleHighlight({ text, color }) {
  const idx = text.indexOf('-');
  if (idx === -1) return <>{text}</>;
  return <>{text.slice(0, idx)}<span style={{ color }}>{text.slice(idx)}</span></>;
}

export default function LoginPage() {
  // Step: 'email' | 'password' | 'code' | 'twofa'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [loginData, setLoginData] = useState(null);
  // Provider code state
  const [providerToken, setProviderToken] = useState('');
  const [providerCode, setProviderCode] = useState('');
  const [providerEmailHint, setProviderEmailHint] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // Admin 2FA state
  const [twofaToken, setTwofaToken] = useState('');
  const [twofaCode, setTwofaCode] = useState('');
  const [twofaEmailHint, setTwofaEmailHint] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  // Design config
  const [primaryColor, setPrimaryColor] = useState('#0092B5');
  const [loginBg, setLoginBg] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [texts, setTexts] = useState({
    logo_title: '', logo_subtitle: '', form_heading: '',
    hero_title: '', hero_subtitle: '', hero_description: '',
    secondary_pre_text: '', secondary_link_text: '', secondary_link_url: '',
    footer: '',
  });
  const navigate = useNavigate();

  React.useEffect(() => {
    if (localStorage.getItem('bgpr_token')) { navigate('/app'); return; }
    fetch(`${API}/api/settings/design-config/public`).then(r => r.json()).then(d => {
      if (d?.title) document.title = d.title;
      if (d?.primary) setPrimaryColor(d.primary);
      if (d?.login_bg) setLoginBg(`${API}${d.login_bg}`);
      else setLoginBg('');
      if (d?.login_texts) setTexts(prev => ({ ...prev, ...d.login_texts }));
      setConfigLoaded(true);
    }).catch(() => setConfigLoaded(true));
  }, [navigate]);

  const completeLogin = (data) => {
    localStorage.setItem('bgpr_token', data.token);
    localStorage.setItem('bgpr_user_type', data.user_type);
    localStorage.setItem('bgpr_user_id', data.user_id);
    localStorage.setItem('bgpr_email', data.email || '');
    if (data.trusted_device_token) {
      localStorage.setItem('bgpr_trusted_device', data.trusted_device_token);
    }
    navigate('/app');
  };

  // Step 1: Identify user type
  const handleIdentify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      let data;
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Unerwarteter Serverfehler' }; }
      if (!res.ok) throw new Error(data.detail || 'Fehler bei der Identifizierung');

      if (data.flow === 'password') {
        setStep('password');
      } else if (data.flow === 'code') {
        setProviderToken(data.temp_token);
        setProviderEmailHint(data.email_hint || '');
        setStep('code');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2a: Admin password login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email, password,
          trusted_device_token: localStorage.getItem('bgpr_trusted_device') || ''
        })
      });
      let data;
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Unerwarteter Serverfehler' }; }
      if (!res.ok) throw new Error(data.detail || 'Login fehlgeschlagen');

      if (data.requires_2fa) {
        setTwofaToken(data.twofa_token);
        setTwofaEmailHint(data.email_hint || '');
        if (data.mail_sent === false) {
          setError('Der Anmeldecode konnte nicht per E-Mail gesendet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Administrator.');
        }
        setStep('twofa');
        setLoading(false);
        return;
      }

      if (data.must_change_password) {
        setTempToken(data.token);
        setLoginData(data);
        setMustChangePassword(true);
      } else {
        completeLogin(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2b: Provider code verification
  const handleVerifyProviderCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-provider-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: providerToken, code: providerCode })
      });
      let data;
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Unerwarteter Serverfehler' }; }
      if (!res.ok) throw new Error(data.detail || 'Verifizierung fehlgeschlagen');
      completeLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Admin 2FA verification
  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ twofa_token: twofaToken, code: twofaCode, trust_device: trustDevice })
      });
      let data;
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Unerwarteter Serverfehler' }; }
      if (!res.ok) throw new Error(data.detail || 'Verifizierung fehlgeschlagen');

      if (data.must_change_password) {
        setTempToken(data.token);
        setLoginData(data);
        setMustChangePassword(true);
        setStep('email');
      } else {
        completeLogin(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend provider code
  const handleResendProviderCode = async () => {
    setResending(true);
    setResent(false);
    try {
      const res = await fetch(`${API}/api/auth/resend-provider-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: providerToken })
      });
      let data;
      const text = await res.text();
      try { data = JSON.parse(text); } catch { data = { detail: text || 'Fehler' }; }
      if (res.ok) {
        if (data.temp_token) setProviderToken(data.temp_token);
        setResent(true);
        setTimeout(() => setResent(false), 5000);
      } else {
        setError(data.detail || 'Fehler beim erneuten Senden');
      }
    } catch { setError('Fehler beim erneuten Senden'); }
    setResending(false);
  };

  // Resend admin 2FA code
  const handleResend2fa = async () => {
    setResending(true);
    setResent(false);
    try {
      const res = await fetch(`${API}/api/auth/resend-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ twofa_token: twofaToken })
      });
      const data = await res.json();
      if (res.ok) {
        setResent(true);
        setTimeout(() => setResent(false), 5000);
      } else {
        setError(data.detail || 'Fehler beim erneuten Senden');
      }
    } catch { setError('Fehler beim erneuten Senden'); }
    setResending(false);
  };

  const goBack = () => {
    setStep('email');
    setPassword('');
    setProviderCode('');
    setTwofaCode('');
    setError('');
    setResent(false);
  };

  if (mustChangePassword) {
    return <ForcePasswordChange token={tempToken} onComplete={() => completeLogin(loginData)} />;
  }

  if (!configLoaded) {
    return <div style={{ minHeight: '100vh', background: '#fff' }} />;
  }

  const year = new Date().getFullYear();
  const footerText = (texts.footer || '').replace(/\{year\}/g, year);

  // ── Helper: Hero panel (right side) ──
  const heroIcon = step === 'code' || step === 'twofa'
    ? <Shield size={36} color="#fff" />
    : <Heart size={36} color="#fff" fill="#fff" />;
  const heroTitle = step === 'code'
    ? 'Sicherheit zuerst'
    : step === 'twofa'
      ? 'Sicherheit zuerst'
      : (texts.hero_title || 'BGPR Mitglieder-Portal');
  const heroSubtitle = step === 'code'
    ? 'ANMELDUNG PER E-MAIL-CODE'
    : step === 'twofa'
      ? 'ZWEI-FAKTOR-AUTHENTIFIZIERUNG'
      : (texts.hero_subtitle || 'Mitgliederportal').toUpperCase();
  const heroDescription = step === 'code'
    ? 'Ihr persönlicher Anmeldecode wurde per E-Mail an Sie gesendet.'
    : step === 'twofa'
      ? 'Ein zusätzlicher Schutz für Ihren Account. Der Code wurde per E-Mail an Sie gesendet.'
      : (texts.hero_description || '');

  // ── Render ──
  return (
    <div data-testid="login-page" style={S.wrapper}>
      <div style={S.left}>
        {/* Brand */}
        <div style={S.logo} data-testid="login-brand">
          <div style={{ ...S.logoIcon, background: primaryColor }}>
            <Heart size={18} color="#fff" fill="#fff" />
          </div>
          <div style={{ lineHeight: 1.15 }}>
            <div style={S.logoTitle}>
              <TitleHighlight text={texts.logo_title || 'BGPR Mitglieder-Portal'} color={primaryColor} />
            </div>
            <div style={S.logoSub}>{texts.logo_subtitle || 'Mitgliederportal'}</div>
          </div>
        </div>

        <div style={S.formArea}>

          {/* ── STEP: EMAIL ── */}
          {step === 'email' && (
            <>
              <h1 style={S.heading} data-testid="login-heading">
                {(texts.form_heading || 'Anmelden').toUpperCase()}
              </h1>
              <div style={{ ...S.headingBar, background: primaryColor }} />
              <div style={S.headingDivider} />

              {error && <div style={S.error} data-testid="login-error">{error}</div>}

              <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, marginTop: 20, marginBottom: 0 }}
                data-testid="login-info-hint">
                Geben Sie Ihre bei der BGPR hinterlegte E-Mail-Adresse ein. Sie erhalten einen Anmeldecode per E-Mail.
              </p>

              <form onSubmit={handleIdentify} style={{ marginTop: 20 }}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>E-MAIL</label>
                  <input style={S.input} data-testid="login-email"
                    placeholder="ihre@email.de" value={email}
                    onChange={e => setEmail(e.target.value)} required autoFocus />
                </div>

                <button type="submit" disabled={loading} data-testid="login-submit"
                  style={{ ...S.submitBtn, background: primaryColor, opacity: loading ? 0.7 : 1 }}>
                  {loading ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} />WIRD GEPRÜFT...</> : 'WEITER'}
                </button>
              </form>

              <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 20, lineHeight: 1.6, textAlign: 'center' }}
                data-testid="login-provider-hint">
                E-Mail nicht mehr aktuell?{' '}
                <span style={{ color: '#6B7280' }}>Kontaktieren Sie die BGPR-Geschäftsstelle.</span>
              </p>
            </>
          )}

          {/* ── STEP: PASSWORD (Admin) ── */}
          {step === 'password' && (
            <>
              <h1 style={S.heading} data-testid="login-heading">ANMELDEN</h1>
              <div style={{ ...S.headingBar, background: primaryColor }} />
              <div style={S.headingDivider} />

              {error && <div style={S.error} data-testid="login-error">{error}</div>}

              <form onSubmit={handleLogin} style={{ marginTop: 32 }}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>E-MAIL</label>
                  <input style={{ ...S.input, background: '#F3F4F6', color: '#6B7280' }}
                    value={email} readOnly data-testid="login-email-display" />
                </div>

                <div style={S.fieldGroup}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <label style={S.label}>PASSWORT</label>
                    <a href="/forgot-password" style={S.forgotLink} data-testid="login-forgot-link">Passwort vergessen?</a>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input style={S.input} data-testid="login-password"
                      type={showPw ? 'text' : 'password'} placeholder="Passwort"
                      value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
                    <button type="button" onClick={() => setShowPw(!showPw)} style={S.eyeBtn} tabIndex={-1}>
                      {showPw ? <EyeOff size={16} color="#9CA3AF" /> : <Eye size={16} color="#9CA3AF" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} data-testid="login-password-submit"
                  style={{ ...S.submitBtn, background: primaryColor, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'ANMELDUNG...' : 'ANMELDEN'}
                </button>
              </form>

              <button onClick={goBack} data-testid="login-back-btn"
                style={{ ...S.backLink, color: '#9CA3AF', marginTop: 16 }}>
                <ArrowLeft size={14} /> Andere E-Mail verwenden
              </button>
            </>
          )}

          {/* ── STEP: PROVIDER CODE ── */}
          {step === 'code' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: '#F0FDF4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <Mail size={28} color="#059669" />
                </div>
                <h1 style={{ ...S.heading, fontSize: 16, letterSpacing: 1.5, textAlign: 'center' }}>ANMELDECODE EINGEBEN</h1>
                <div style={{ ...S.headingBar, background: primaryColor, margin: '12px auto 0' }} />
              </div>

              <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, textAlign: 'center', margin: '0 0 8px' }}
                data-testid="provider-code-hint">
                Ein Anmeldecode wurde an Ihre E-Mail-Adresse gesendet. Bitte geben Sie diesen ein.
              </p>
              <p style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', margin: '0 0 24px' }}>
                Code gesendet an <strong style={{ color: '#374151' }}>{providerEmailHint}</strong>
              </p>

              {error && <div style={S.error} data-testid="provider-code-error">{error}</div>}
              {resent && <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 500, textAlign: 'center' }}>Neuer Code wurde gesendet.</div>}

              <form onSubmit={handleVerifyProviderCode}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>ANMELDECODE</label>
                  <input style={{ ...S.input, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
                    data-testid="provider-code-input"
                    placeholder="000000" value={providerCode} maxLength={6}
                    onChange={e => setProviderCode(e.target.value.replace(/[^0-9]/g, ''))}
                    autoFocus required />
                </div>

                <button type="submit" disabled={loading} data-testid="provider-code-submit"
                  style={{ ...S.submitBtn, background: primaryColor, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'PRÜFE...' : 'ANMELDEN'}
                </button>
              </form>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button onClick={handleResendProviderCode} disabled={resending}
                  data-testid="provider-resend-btn"
                  style={{ ...S.backLink, color: primaryColor }}>
                  <RefreshCw size={14} /> {resending ? 'Wird gesendet...' : 'Code erneut senden'}
                </button>
                <button onClick={goBack} data-testid="provider-back-btn"
                  style={{ ...S.backLink, color: '#9CA3AF' }}>
                  Zurück
                </button>
              </div>

              <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 24, lineHeight: 1.6, textAlign: 'center' }}>
                E-Mail nicht mehr aktuell? <span style={{ color: '#6B7280' }}>Kontaktieren Sie die BGPR-Geschäftsstelle.</span>
              </p>
            </>
          )}

          {/* ── STEP: ADMIN 2FA ── */}
          {step === 'twofa' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: '#F0FDF4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <Shield size={28} color="#059669" />
                </div>
                <h1 style={{ ...S.heading, fontSize: 16, letterSpacing: 1.5, textAlign: 'center' }}>ZWEI-FAKTOR-AUTHENTIFIZIERUNG</h1>
                <div style={{ ...S.headingBar, background: primaryColor, margin: '12px auto 0' }} />
              </div>

              <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, textAlign: 'center', margin: '0 0 24px' }}>
                Ein 6-stelliger Code wurde an <strong>{twofaEmailHint}</strong> gesendet. Geben Sie diesen ein, um die Anmeldung abzuschließen.
              </p>

              {error && <div style={S.error} data-testid="twofa-error">{error}</div>}
              {resent && <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 500, textAlign: 'center' }}>Neuer Code wurde gesendet.</div>}

              <form onSubmit={handleVerify2fa}>
                <div style={S.fieldGroup}>
                  <label style={S.label}>ANMELDECODE</label>
                  <input style={{ ...S.input, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
                    data-testid="twofa-code-input"
                    placeholder="000000" value={twofaCode} maxLength={12}
                    onChange={e => setTwofaCode(e.target.value.replace(/[^0-9a-fA-F-]/g, ''))}
                    autoFocus required />
                  <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6, textAlign: 'center' }}>
                    Alternativ können Sie einen Notfallcode eingeben.
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer' }}
                  onClick={() => setTrustDevice(!trustDevice)} data-testid="twofa-trust-device">
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${trustDevice ? primaryColor : '#D1D5DB'}`, background: trustDevice ? primaryColor : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', flexShrink: 0 }}>
                    {trustDevice && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                  </div>
                  <span style={{ fontSize: 13, color: '#374151' }}>Diesem Gerät 30 Tage vertrauen</span>
                </div>

                <button type="submit" disabled={loading} data-testid="twofa-submit"
                  style={{ ...S.submitBtn, background: primaryColor, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'PRÜFE...' : 'CODE BESTÄTIGEN'}
                </button>
              </form>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button onClick={handleResend2fa} disabled={resending}
                  data-testid="twofa-resend-btn"
                  style={{ ...S.backLink, color: primaryColor }}>
                  <RefreshCw size={14} /> {resending ? 'Wird gesendet...' : 'Code erneut senden'}
                </button>
                <button onClick={goBack} data-testid="twofa-back-btn"
                  style={{ ...S.backLink, color: '#9CA3AF' }}>
                  Zurück zum Login
                </button>
              </div>
            </>
          )}
        </div>

        <div style={S.footer} data-testid="login-footer">{footerText}</div>
      </div>

      {/* RIGHT: Hero Image */}
      <div style={S.right}>
        {loginBg && <div style={{ ...S.heroBg, backgroundImage: `url("${loginBg}")` }} />}
        {!loginBg && <div style={{ ...S.heroBg, background: '#5f8a8b' }} />}
        <div style={S.heroTint} />
        <div style={S.heroRadial} />
        <div style={S.heroContent}>
          <div style={S.heroIcon}>{heroIcon}</div>
          <h2 style={S.heroTitle}>
            {step === 'code' || step === 'twofa'
              ? heroTitle
              : <TitleHighlight text={heroTitle} color="rgba(255,255,255,0.90)" />}
          </h2>
          <div style={{ ...S.heroBar, background: primaryColor }} />
          <p style={S.heroSub}>{heroSubtitle}</p>
          <p style={S.heroDesc}>{heroDescription}</p>
        </div>
      </div>
    </div>
  );
}

const S = {
  wrapper: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" },
  left: { flex: '0 0 50%', display: 'flex', flexDirection: 'column', padding: '40px 60px', background: '#fff', position: 'relative' },
  logo: { display: 'flex', alignItems: 'center', gap: 12 },
  logoIcon: { width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoTitle: { fontSize: 16, fontWeight: 800, color: '#1a2035', lineHeight: 1.2 },
  logoSub: { fontSize: 9, fontWeight: 700, letterSpacing: 2.5, color: '#9CA3AF', textTransform: 'uppercase', marginTop: 2 },
  formArea: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 400, width: '100%' },
  heading: { fontSize: 15, fontWeight: 800, letterSpacing: 3, color: '#1a2035', margin: 0 },
  headingBar: { width: 36, height: 3, borderRadius: 2, marginTop: 12 },
  headingDivider: { width: '100%', height: 1, background: '#E5E7EB', marginTop: 12 },
  error: { background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, marginTop: 20, marginBottom: 16, fontSize: 13, fontWeight: 500 },
  fieldGroup: { marginBottom: 20 },
  label: { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 2, color: '#6B7280', marginBottom: 8, textTransform: 'uppercase' },
  input: { width: '100%', padding: '12px 16px', fontSize: 14, border: '1px solid #E5E7EB', borderRadius: 8, outline: 'none', color: '#1a2035', background: '#FAFBFC', boxSizing: 'border-box', transition: 'border-color 0.2s' },
  eyeBtn: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
  forgotLink: { fontSize: 12, color: '#9CA3AF', textDecoration: 'none', fontWeight: 500 },
  submitBtn: { width: '100%', padding: '14px', fontSize: 13, fontWeight: 700, letterSpacing: 2, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', textTransform: 'uppercase', marginTop: 8, transition: 'opacity 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  backLink: { background: 'none', border: 'none', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500 },
  footer: { fontSize: 11, color: '#9CA3AF', lineHeight: 1.5, textAlign: 'center' },
  right: { flex: '0 0 50%', position: 'relative', overflow: 'hidden' },
  heroBg: { position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'right center' },
  heroTint: { position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)' },
  heroRadial: { position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(55% 60% at 30% 50%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)' },
  heroContent: { position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', padding: '60px 50px', textShadow: '0 2px 12px rgba(0,0,0,0.55)' },
  heroIcon: { width: 64, height: 64, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24, background: 'rgba(255,255,255,0.10)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.20)' },
  heroTitle: { fontSize: 40, fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1.1, letterSpacing: '-0.01em' },
  heroBar: { width: 48, height: 3, borderRadius: 2, marginTop: 16, marginBottom: 16 },
  heroSub: { fontSize: 12, fontWeight: 700, letterSpacing: 4, color: 'rgba(255,255,255,0.90)', textTransform: 'uppercase', marginBottom: 24 },
  heroDesc: { fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, maxWidth: 400 },
};
